import * as Sentry from '@sentry/react';

import { reportError } from '../errors/reportError';

const UNMASK_SELECTORS = [
  '[data-replay-allow]',
  'button',
  'a',
  'nav *',
  '[role="menuitem"]',
];

type ReplayHandle = NonNullable<ReturnType<typeof Sentry.getReplay>>;

// The SDK never rejects flush() on upload failure: it reports through the
// integration-level onError callback, stops the replay, and resolves. This is
// the only signal that lets flushReplay() tell "nothing buffered" apart from
// "upload failed" (retryable).
let lastReplayError: unknown = null;

export function initReplay() {
  return Sentry.replayIntegration({
    maskAllText: true,
    maskAllInputs: true,
    unmask: UNMASK_SELECTORS,
    maxReplayDuration: 180_000,
    onError: (error) => {
      lastReplayError = error;
    },
  });
}

/**
 * Start the always-on rolling replay buffer. Called once at app boot, right
 * after Sentry.init. Buffer mode records locally and uploads nothing until
 * flushReplay() sends it.
 */
export function startReplayBuffer(): void {
  const replay = Sentry.getReplay();
  if (!replay) {
    // Sentry is not initialized (no DSN configured) — replay cannot run.
    console.info('[replay] buffering not started - replay integration unavailable');
    return;
  }
  replay.startBuffering();
}

export type FlushReplayFailureReason =
  | 'unavailable'
  | 'empty-buffer'
  | 'send-failed'
  | 'sdk-error';

export type FlushReplayResult =
  | { ok: true; replayId: string; route: string }
  | { ok: false; reason: FlushReplayFailureReason; retryable: boolean; route: string };

/**
 * Upload the current buffer contents and return to buffer mode.
 *
 * The route is captured here, at flush time — the user may have navigated
 * since the buffer started, so the recording-start route would be wrong.
 */
export async function flushReplay({
  conversationId = null,
}: {
  conversationId?: number | null;
} = {}): Promise<FlushReplayResult> {
  const route = window.location.pathname;
  const replay = Sentry.getReplay();
  if (!replay) {
    console.info('[replay] flush no-op - replay integration unavailable', { conversationId });
    return { ok: false, reason: 'unavailable', retryable: false, route };
  }

  lastReplayError = null;
  let replayId: string | null = null;
  try {
    // continueRecording: false — otherwise the SDK converts the buffer into a
    // continuously-uploading session replay after the flush.
    await replay.flush({ continueRecording: false });
    // Read before restartBuffer(): stop() tears down the session and id.
    replayId = replay.getReplayId() ?? null;
  } catch (err) {
    reportError(err, 'replay.flush', {
      level: 'warning',
      extra: { conversationId },
    });
    await restartBuffer(replay, conversationId);
    return { ok: false, reason: 'sdk-error', retryable: false, route };
  }

  const sendError = lastReplayError;
  lastReplayError = null;
  await restartBuffer(replay, conversationId);

  if (sendError != null) {
    // Upload failed after SDK-internal retries; the buffer content is gone,
    // but a fresh buffer is recording — retrying captures what happens next.
    reportError(sendError, 'replay.flush.send', {
      level: 'warning',
      extra: { conversationId },
    });
    return { ok: false, reason: 'send-failed', retryable: true, route };
  }

  if (!replayId) {
    console.info('[replay] flush no-op - empty buffer', { conversationId });
    return { ok: false, reason: 'empty-buffer', retryable: false, route };
  }

  return { ok: true, replayId, route };
}

/**
 * Re-enter buffer mode after a flush. startBuffering() is a no-op while the
 * replay is still enabled, so a successful flush needs stop() first; after an
 * upload failure the SDK has already stopped it and stop() is a safe no-op.
 */
async function restartBuffer(replay: ReplayHandle, conversationId: number | null): Promise<void> {
  try {
    await replay.stop();
    replay.startBuffering();
  } catch (err) {
    reportError(err, 'replay.buffer.restart', {
      level: 'warning',
      extra: { conversationId },
    });
  }
}
